/**
 * Compact numbers with units, localised by Intl rather than by translation keys: "3m" in English,
 * "3 Min." in German, "3分" in Japanese.
 */
type Unit = 'second' | 'minute' | 'hour' | 'day'

function unitFormat(lang: string, unit: Unit, value: number): string {
    try {
        return new Intl.NumberFormat(lang, { style: 'unit', unit, unitDisplay: 'narrow' }).format(value)
    } catch {
        return `${value}${unit[0]}`
    }
}

/** How long ago, as the prototype's time column: now, 12s, 3m, 2h, 4d. */
export function compactAgo(iso: string, lang: string, nowLabel: string, now = Date.now()): string {
    const then = Date.parse(iso)
    if (Number.isNaN(then)) return ''
    const s = Math.max(0, Math.round((now - then) / 1000))
    if (s < 5) return nowLabel
    if (s < 60) return unitFormat(lang, 'second', s)
    const m = Math.round(s / 60)
    if (m < 60) return unitFormat(lang, 'minute', m)
    const h = Math.round(m / 60)
    if (h < 24) return unitFormat(lang, 'hour', h)
    return unitFormat(lang, 'day', Math.round(h / 24))
}

/** A duration in minutes: "45m", "1h 05m" in English. Negative values keep their sign. */
export function formatMinutes(minutes: number, lang: string): string {
    if (!Number.isFinite(minutes)) return ''
    const sign = minutes < 0 ? '-' : ''
    const total = Math.round(Math.abs(minutes))
    const h = Math.floor(total / 60)
    const m = total % 60
    if (h === 0) return sign + unitFormat(lang, 'minute', m)
    const mins = unitFormat(lang, 'minute', m)
    // Pad the minutes the way the prototype does ("1h 05m") where the locale writes plain digits.
    const padded = m < 10 && mins.startsWith(String(m)) ? '0' + mins : mins
    return `${sign}${unitFormat(lang, 'hour', h)} ${padded}`
}

/** A signed deviation: "+1h 20m" for a slip, "-15m" for a gain. */
export function formatSignedMinutes(minutes: number, lang: string): string {
    if (!Number.isFinite(minutes)) return ''
    return (minutes > 0 ? '+' : '') + formatMinutes(minutes, lang)
}

/** The message id as the prototype shows it: #000123. */
export const formatMessageId = (id: number) => '#' + String(id).padStart(6, '0')

/** The words of a search, as the server matches them: whitespace-separated, all must match. */
export function searchWords(q: string): string[] {
    return q.trim().split(/\s+/).filter(Boolean)
}
